import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { register } from '../../services/authService';
import AuthShell from './AuthShell';
import { beginMicrosoftSignIn, getPublicAuthMode } from './authAccess';
import './Auth.css';

const developmentHighlights = [
  {
    label: 'Estimator-only',
    text: 'Self-serve registration in development always creates estimator access.',
  },
  {
    label: 'Local testing only',
    text: 'This path exists to unblock local UX and API work without changing the production access model.',
  },
  {
    label: 'Microsoft stays primary',
    text: 'Public production access still belongs to Island Millwork Microsoft identity.',
  },
];

const managedHighlights = [
  {
    label: 'Provisioned centrally',
    text: 'Public account creation is not available when Microsoft-managed access is active.',
  },
  {
    label: 'Estimator workflows',
    text: 'Role assignment should happen through the org-managed access layer, not self-serve signup.',
  },
  {
    label: 'One front door',
    text: 'Users should return to the shared sign-in flow instead of branching into a separate registration UX.',
  },
];

const RegisterForm = () => {
  const authMode = getPublicAuthMode();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);

    try {
      const { confirmPassword, ...payload } = formData;
      await register(payload);

      navigate('/login', {
        replace: true,
        state: {
          notice:
            'Local estimator account created. Sign in with those credentials to continue.',
        },
      });
    } catch (err) {
      if (err.errors) {
        const errorMessages = Object.entries(err.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('; ');
        setError(errorMessages);
      } else {
        setError(err.message || 'Registration failed.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError('');
    setIsMicrosoftLoading(true);

    try {
      await beginMicrosoftSignIn();
    } catch (err) {
      setError(err?.message || 'Microsoft sign-in could not be started.');
      setIsMicrosoftLoading(false);
    }
  };

  if (!authMode.canSelfRegister) {
    return (
      <AuthShell
        eyebrow="Access Provisioning"
        title="Public registration is disabled"
        description="Self-serve signup is reserved for local development only. In Microsoft-managed workspaces, access is provisioned through Island Millwork identity."
        helper="If you need access, return to sign in or ask an administrator to provision your organization account."
        statusLabel={authMode.statusLabel}
        highlights={managedHighlights}
        asideTitle="Public access should not fork away from the org-managed sign-in flow."
      >
        {error ? <div className="auth-error">{error}</div> : null}

        <div className="auth-message-card">
          <p className="auth-message-card__title">Use the shared sign-in path</p>
          <p className="auth-message-card__body">
            Registration is unavailable in this environment, and any enabled
            self-serve path is estimator-only by design.
          </p>
        </div>

        <div className="auth-action-stack">
          {authMode.canStartMicrosoftSignIn ? (
            <button
              type="button"
              className="auth-button auth-button--primary auth-button--microsoft"
              onClick={handleMicrosoftLogin}
              disabled={isMicrosoftLoading}
            >
              {isMicrosoftLoading ? 'Redirecting to Microsoft...' : 'Continue with Microsoft'}
            </button>
          ) : null}

          <Link to="/login" className="auth-text-link">
            Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      eyebrow="Local Development Registration"
      title="Create a local estimator account"
      description="This self-serve path is for development workspaces only. New accounts created here are always scoped to estimator access."
      helper="Need admin access for development? Use an existing seeded admin account or the Microsoft-managed path instead of self-signup."
      statusLabel={authMode.statusLabel}
      highlights={developmentHighlights}
      asideTitle="Keep development moving without widening production access."
    >
      {error ? <div className="auth-error">{error}</div> : null}

      <form onSubmit={handleSubmit} className="auth-form">
        <div className="auth-field">
          <label htmlFor="username" className="auth-label">
            Username
          </label>
          <input
            id="username"
            name="username"
            type="text"
            autoComplete="username"
            value={formData.username}
            onChange={handleChange}
            className="auth-input"
            required
          />
        </div>

        <div className="auth-field">
          <label htmlFor="email" className="auth-label">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
            className="auth-input"
            required
          />
        </div>

        <div className="auth-field">
          <label htmlFor="password" className="auth-label">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            value={formData.password}
            onChange={handleChange}
            className="auth-input"
            required
            minLength="8"
          />
        </div>

        <div className="auth-field">
          <label htmlFor="confirmPassword" className="auth-label">
            Confirm password
          </label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            value={formData.confirmPassword}
            onChange={handleChange}
            className="auth-input"
            required
            minLength="8"
          />
        </div>

        <button
          type="submit"
          className="auth-button auth-button--primary"
          disabled={isLoading}
        >
          {isLoading ? 'Creating account...' : 'Create estimator account'}
        </button>
      </form>

      <div className="auth-links">
        <span>Already have an account?</span>
        <Link to="/login" className="auth-inline-link">
          Back to sign in
        </Link>
      </div>
    </AuthShell>
  );
};

export default RegisterForm;
